/**
 * DueDiligenceVault — tiered document vault for the commercial PDP.
 *
 * Data source: propertyDocumentsInformation.commercialDocumentList
 *   Shape: { [folderName]: { [docKey]: { documentName, documentUrl, fileListId } } }
 *
 * Tier assignment (by folder name):
 *   Tier 1 (open access): "The Offering", "Purchase & Sale Agreement"
 *   Tier 2 (NDA required): all other folders
 *
 * Progress bar logic:
 *   - On init: reads documentReadPercentage from pdpInitData (null = 0%)
 *   - On Tier 2 document click: calculates % based on (accessed docs / total tier2 docs * 100)
 *     and calls POST /api/portal/save-document-read-percentage
 *
 * Green tick logic:
 *   - Folder shows green tick when ALL docs inside it have been accessed in the session
 *   - Tier 1: no default tick on header; per-folder tick only
 *   - Tier 2: outer box turns green border on NDA acceptance; per-folder tick only
 */
import { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { saveDocumentReadPercentage, savePropertyDocumentUserRead, downloadCommercialDocument } from '@/api/PDP';
import { setLoginModal, setLoginTab, setLoginCallLocation } from '@/redux/login/loginSlice';
import { trackButtonClick } from '@/utils/analytics';
import { buildEventLabel } from './utils';

import ProgressBar from './ProgressBar';
import Tier1Section from './Tier1Section';
import Tier2Section from './Tier2Section';
import NDAModel from '../BidCenter/NDAModel';
import './due-diligence-vault.css';

// ── Constants ──────────────────────────────────────────────────────────────────
const TIER1_FOLDER_NAMES = new Set([
  'The Offering',
  'Purchase & Sale Agreement',
  'COM_THE_OFFERING',
  'COM_OFFERING',
  'COM_PURCHASE_AND_SALE_AGREEMENT',
  'COM_PURCHASE_SALE_AGREEMENT',
  'COMMERCIAL_OTHER',
]);

// ── Component ──────────────────────────────────────────────────────────────────
const DueDiligenceVault = ({
  pdpDocuments,
  propertyDocumentsInformation,
  address,
  isNDASigned,
}) => {
  const dispatch = useDispatch();
  const isNDAAccepted = useSelector((state) => state.pdp.isNDAAccepted);
  const user = useSelector((state) => state.user.user);
  const pdpInitData = useSelector((state) => state.pdp.pdpInitData);
  const propertyId = pdpInitData?.propertyDetails?.propertyId;

  // Use commercialDocumentList from API response
  const commercialDocumentList =
    propertyDocumentsInformation?.commercialDocumentList ?? {};
  const allFolderEntries = Object.entries(commercialDocumentList);
  // Check if a folder key belongs to Tier 1 (open access)
  const isTier1 = (key) => TIER1_FOLDER_NAMES.has(key);
  const tier1Folders = allFolderEntries
    .filter(([name, docsMap]) => isTier1(name) && Object.keys(docsMap || {}).length > 0)
    .sort((a, b) => (a[0] === 'COMMERCIAL_OTHER') - (b[0] === 'COMMERCIAL_OTHER'));
  const tier2Folders = allFolderEntries.filter(
    ([name, docsMap]) => !isTier1(name) && Object.keys(docsMap || {}).length > 0
  );

  // Count total Tier 2 documents for progress calculation
  const totalTier2Docs = tier2Folders.reduce((count, [, docsMap]) => {
    return count + Object.keys(docsMap || {}).length;
  }, 0);

  // ── State ────────────────────────────────────────────────────────────────────
  const [progressPercent, setProgressPercent] = useState(0);
  const [showNDAModal, setShowNDAModal] = useState(false);
  const [fileErrors, setFileErrors] = useState(new Map());
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  // Track which documents have been accessed (by fileKey) in this session
  const [accessedDocs, setAccessedDocs] = useState(new Set());
  const sessionAccessedDocs = useRef(new Set());
  const pendingNDAAfterLogin = useRef(false);
  const initializedRef = useRef(false);

  // ── Effects ──────────────────────────────────────────────────────────────────
  // Initialize accessedDocs from hasUserReadDocument field + calculate progress
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    // Seed accessedDocs from hasUserReadDocument in the API data
    const preAccessedKeys = new Set();
    for (const [, docsMap] of allFolderEntries) {
      for (const doc of Object.values(docsMap || {})) {
        if (doc.hasUserReadDocument != null) {
          const fileKey = doc.fileListId || doc.documentName;
          preAccessedKeys.add(fileKey);
        }
      }
    }

    if (preAccessedKeys.size > 0) {
      setAccessedDocs(preAccessedKeys);
      // Also seed sessionAccessedDocs so we don't re-POST for already-read docs
      preAccessedKeys.forEach((key) => sessionAccessedDocs.current.add(key));
    }

    // Calculate initial progress from Tier 2 docs read
    if (totalTier2Docs > 0) {
      let tier2ReadCount = 0;
      for (const [, docsMap] of tier2Folders) {
        for (const doc of Object.values(docsMap || {})) {
          if (doc.hasUserReadDocument != null) {
            tier2ReadCount++;
          }
        }
      }
      const initialPercent = Math.min(100, Math.round((tier2ReadCount / totalTier2Docs) * 100));
      setProgressPercent(initialPercent);

      // Sync DB if stored percentage is stale (e.g., new docs were added on backend)
      const savedPercent = pdpInitData?.documentReadPercentage;
      const storedPercent = savedPercent != null ? Number(savedPercent) : 0;
      const userLoggedIn = user && Object.keys(user).length > 0;
      if (propertyId && userLoggedIn && storedPercent !== initialPercent) {
        saveDocumentReadPercentage(propertyId, initialPercent);
      }
    } else {
      // Fallback to saved percentage from pdpInitData
      const savedPercent = pdpInitData?.documentReadPercentage;
      setProgressPercent(savedPercent != null ? Number(savedPercent) : 0);
    }
  }, [allFolderEntries, tier2Folders, totalTier2Docs, pdpInitData, user, propertyId]);

  // After user logs in via Unlock Vault → auto-show NDA modal
  useEffect(() => {
    const isLoggedIn = user && Object.keys(user).length > 0;
    if (pendingNDAAfterLogin.current && isLoggedIn && isNDAAccepted !== 'Y') {
      pendingNDAAfterLogin.current = false;
      setShowNDAModal(true);
    }
  }, [user, isNDAAccepted]);

  // Hide when no documents
  if (allFolderEntries.length === 0) return null;

  // ── Helpers ──────────────────────────────────────────────────────────────────
  // Check if all docs in a folder have been accessed (only valid when logged in)
  const isLoggedIn = user && Object.keys(user).length > 0;

  const isFolderComplete = (docsMap) => {
    if (!isLoggedIn) return false;
    const docs = Object.values(docsMap || {});
    if (docs.length === 0) return false;
    return docs.every((doc) => {
      const fileKey = doc.fileListId || doc.documentName;
      return accessedDocs.has(fileKey);
    });
  };

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleFolderOpen = (folderName) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      next.has(folderName) ? next.delete(folderName) : next.add(folderName);
      return next;
    });
  };

  const handleFileActivate = async (doc, isTier2 = false) => {
    if (isTier2 && (!isLoggedIn || isNDAAccepted !== 'Y')) return;
    const fileKey = doc.fileListId || doc.documentName;

    try {
      // Tier 2: prefer opening URL in new tab
      // Tier 1: prefer downloading via fileListId
      if (isTier2 && doc.documentUrl) {
        window.open(doc.documentUrl, '_blank', 'noopener noreferrer');
      } else if (doc.fileListId) {
        await downloadCommercialDocument(doc);
      } else if (doc.documentUrl) {
        window.open(doc.documentUrl, '_blank', 'noopener noreferrer');
      }

      trackButtonClick({
        eventName: "document_download",
        eventCategory: "NEW-PDP",
        eventSource: "on click",
        eventLabel: buildEventLabel(pdpInitData),
      });
      setFileErrors((prev) => { const n = new Map(prev); n.delete(fileKey); return n; });

      // Track document access for green tick (both tiers if logged in)
      if (isLoggedIn) {
        setAccessedDocs((prev) => {
          const next = new Set(prev);
          next.add(fileKey);
          return next;
        });
      }

      // Update progress for Tier 2 document access only
      if (isTier2 && isLoggedIn && !sessionAccessedDocs.current.has(fileKey)) {
        sessionAccessedDocs.current.add(fileKey);

        // Mark this file as read on the backend
        if (propertyId && doc.fileListId) {
          savePropertyDocumentUserRead(propertyId, doc.fileListId);
        }

        // Calculate new percentage based on total tier2 docs accessed
        if (totalTier2Docs > 0) {
          const accessedCount = sessionAccessedDocs.current.size;
          const newPercent = accessedCount >= totalTier2Docs
            ? 100
            : Math.round((accessedCount / totalTier2Docs) * 100);
          setProgressPercent(newPercent);

          // Persist progress percentage to backend
          if (propertyId) {
            saveDocumentReadPercentage(propertyId, newPercent);
          }
        }
      }
    } catch (error) {
      setFileErrors((prev) => { const n = new Map(prev); n.set(fileKey, error?.message ?? 'Download failed'); return n; });
    }
  };

  const handleUnlockVault = () => {
    if (!isLoggedIn) {
      pendingNDAAfterLogin.current = true;
      dispatch(setLoginModal(true));
      dispatch(setLoginTab('loginTab'));
      dispatch(setLoginCallLocation('Popup'));
    } else {
      setShowNDAModal(true);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="ddv-root">
      {/* Header row */}
      <div className="ddv-header">
        <h2 className="ddv-title">Due Diligence Vault</h2>
        {tier2Folders.length > 0 && (
          <ProgressBar percent={Math.min(100, progressPercent)} />
        )}
      </div>

      {/* Tier sections */}
      <div className="ddv-tiers">
        {tier1Folders.length > 0 && (
          <div className="ddv-tier ddv-tier--tier1">
            <Tier1Section
              tier1Folders={tier1Folders}
              expandedFolders={expandedFolders}
              fileErrors={fileErrors}
              onFolderToggle={handleFolderOpen}
              onFileActivate={(doc) => handleFileActivate(doc, false)}
              isFolderComplete={isFolderComplete}
            />
          </div>
        )}
        {tier2Folders.length > 0 && (
          <div className="ddv-tier ddv-tier--tier2">
            <Tier2Section
              isNDAAccepted={isNDAAccepted}
              user={user}
              tier2Folders={tier2Folders}
              expandedFolders={expandedFolders}
              fileErrors={fileErrors}
              onFolderToggle={handleFolderOpen}
              onFileActivate={(doc) => handleFileActivate(doc, true)}
              onUnlockVault={handleUnlockVault}
              isFolderComplete={isFolderComplete}
            />
          </div>
        )}
      </div>

      <NDAModel
        show={showNDAModal}
        userProfileData={user}
        isFormDocuments={true}
        handleClose={() => setShowNDAModal(false)}
        handleNDAccept={() => {
          trackButtonClick({
            eventName: "nda_accepted",
            eventCategory: "NEW-PDP",
            eventSource: "on click",
            eventLabel: buildEventLabel(pdpInitData),
          });
          setShowNDAModal(false);
        }}
      />
    </div>
  );
};

export default DueDiligenceVault;
